<template>
    <div>
        <div class="relative">
            <div
                v-if="loadingData || allMonthDisabled"
                class="absolute inset-s-px inset-e-px inset-be-px w-[calc(100%-2px)] h-[calc(100%-81px)] bg-white/80 flex items-center justify-center z-10 rounded-xl"
            >
                <div v-if="loadingData" class="text-slate-500">
                    <vx-icon-loading class="animate-loading-spin text-2xl"></vx-icon-loading>
                </div>

                <vx-alert v-else-if="allMonthDisabled" color="info" class="max-w-4/5" :closeable="false">
                    <template v-slot:icon><font-awesome-icon :icon="faDiamondExclamation" widthAuto /></template>
                    <span v-if="hasTooSoon">You have reached your max send for this month.</span>
                    <span v-else>Too far in the future to schedule.</span>
                </vx-alert>
            </div>

            <vx-date-time-picker
                ref="datePicker"
                :modelValue="props.modelValue"
                :timezone="getClientTz()"
                :minDate="dayjs().tz(getClientTz()).toDate()"
                :maxDate="maxDate ?? undefined"
                :disabledDates="disabledDates"
                :attributes="dateAttributes"
                :rules="datePickerRules"
                :hourAttributes="hourAttributes"
                :disabledMinutes="disabledMinutes"
                :minuteAttributes="minuteAttributes"
                :isRequired="props.isRequired"
                @update:modelValue="onDateChange"
            >
                <template v-slot:dayPopover="{ day }">
                    <div class="space-y-2 max-w-[196px]">
                        <div v-if="day.isDisabled">
                            <vx-badge size="xs" class="mb-3" color="info"
                                >Blocked
                                <font-awesome-icon class="size-2!" :icon="faOctagon" />
                            </vx-badge>
                            <div v-if="getDayReasons(day)" class="flex flex-col gap-1.5">
                                <p v-for="reason in getDayReasons(day)" :key="reason" class="text-sm">
                                    {{ getReasonMessage(reason, dayjs(day.id).format('YYYY-MM-DD')) }}
                                </p>
                            </div>
                            <p v-else>Corporate has blocked usage in these dates.</p>
                        </div>
                    </div>
                </template>

                <template v-slot:hourPopover>
                    <div class="space-y-2 max-w-[196px]">
                        <vx-badge size="xs" class="mb-3" color="info"
                            >Blocked
                            <font-awesome-icon class="size-2!" :icon="faOctagon" />
                        </vx-badge>
                        <p class="text-sm">{{ REASON_MESSAGES.outside_permitted_window }}</p>
                    </div>
                </template>

                <template v-slot:minutePopover>
                    <div class="space-y-2 max-w-[196px]">
                        <vx-badge size="xs" class="mb-3" color="info"
                            >Blocked
                            <font-awesome-icon class="size-2!" :icon="faOctagon" />
                        </vx-badge>
                        <p class="text-sm">{{ REASON_MESSAGES.outside_permitted_window }}</p>
                    </div>
                </template>
            </vx-date-time-picker>
        </div>
        <div
            v-if="!onCorporate()"
            class="flex py-2 px-3 w-full text-xs text-slate-500 mt-2 items-center justify-center border border-solid border-slate-200 rounded-lg bg-[repeating-linear-gradient(135deg,#F1F5F9_0px,#F1F5F9_6px,white_6px,white_7px)] bg-slate-100"
        >
            Background pattern marks days or times blocked by corporate.
        </div>
    </div>
</template>

<script setup lang="ts">
import { VxAlert, VxBadge, VxDateTimePicker, VxIconLoading } from '@voxie/frontend-components';
import type { VxDateTimeHourAttributeConfig, VxDateTimeMinuteAttributeConfig } from '@voxie/frontend-components';
import { computed, nextTick, ref, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faDiamondExclamation, faOctagon } from '@fortawesome/pro-solid-svg-icons';
import { getClientTz } from '~/utils/date';
import dayjsLib from '~/utils/dayjs';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { REASON_MESSAGES, useSendingCalendar } from './useSendingCalendar';
import { useMessageRules } from '~/components/quick-blast/useMessageRules';

const dayjs = dayjsLib;
const { currentGroup, currentOrgUnit, onCorporate } = useCurrentGroup();
const { isTypeRestricted } = useMessageRules();
const {
    getSendingCalendar,
    rawCalendarDates,
    reservedDateReasons,
    disabledDates,
    dateAttributes,
    allowedHoursByDate,
    blockedHoursByDate,
    reasonsByDate,
    isMonthLoaded,
    isMonthParsed,
} = useSendingCalendar();

const props = withDefaults(
    defineProps<{ modelValue?: Date; campaignType?: 'marketing' | 'transactional'; isRequired?: boolean }>(),
    {
        isRequired: false,
    }
);

const emit = defineEmits<{
    'update:modelValue': [value: Date | undefined];
}>();

const datePicker = ref<typeof VxDateTimePicker | null>();

const campaignTypeRestricted = computed(() => (props.campaignType ? isTypeRestricted(props.campaignType) : false));

const activeMonth = computed(() => {
    const page = datePicker.value?.pages?.[0];
    if (!page?.year || !page?.month) {
        return null;
    }

    return { year: page.year, month: page.month };
});

const loadingData = computed(() => {
    if (onCorporate() || !campaignTypeRestricted.value) {
        return false;
    }

    if (!activeMonth.value) {
        return true;
    }

    return !isMonthLoaded(activeMonth.value) && !isMonthParsed(activeMonth.value);
});

const maxDate = computed(() => {
    if (onCorporate() || !campaignTypeRestricted.value) {
        return null;
    }

    const datesWithTooFarOut = [];
    for (const [dateKey, reasons] of reasonsByDate.value.entries()) {
        if (reasons.has('too_far_out')) {
            datesWithTooFarOut.push(dateKey);
        }
    }

    if (datesWithTooFarOut.length === 0) {
        return null;
    }

    const sortedDates = datesWithTooFarOut.sort();
    const firstTooFarOutDate = sortedDates[0];

    const tz = getClientTz();
    const firstTooFarOutDay = dayjs.tz(firstTooFarOutDate, tz);

    return firstTooFarOutDay.date() === 1 ? firstTooFarOutDay.date(2).toDate() : firstTooFarOutDay.toDate();
});

const hasTooSoon = computed(() => {
    if (onCorporate() || !activeMonth.value || !campaignTypeRestricted.value) {
        return false;
    }

    if (!isMonthLoaded(activeMonth.value) || !isMonthParsed(activeMonth.value)) {
        return false;
    }

    const tz = getClientTz();
    const monthStart = dayjs()
        .tz(tz)
        .year(activeMonth.value.year)
        .month(activeMonth.value.month - 1)
        .startOf('month');
    const monthEnd = monthStart.endOf('month');

    let tooFarOut = false;

    for (const [dateKey, reasons] of reasonsByDate.value.entries()) {
        const date = dayjs.tz(dateKey, tz);
        if (date.isSameOrAfter(monthStart, 'day') && date.isSameOrBefore(monthEnd, 'day')) {
            if (reasons.has('too_soon') || reasons.has('frequency_limit_reached')) {
                return true;
            }

            if (reasons.has('too_far_out')) {
                tooFarOut = true;
            }
        }
    }

    // set to true if a whole month is blocked not for reasons from above
    return !tooFarOut && allMonthDisabled.value;
});

const allMonthDisabled = computed(() => {
    if (onCorporate() || !activeMonth.value || !campaignTypeRestricted.value) {
        return false;
    }

    if (!isMonthLoaded(activeMonth.value) || !isMonthParsed(activeMonth.value)) {
        return false;
    }

    const monthStart = dayjs()
        .year(activeMonth.value.year)
        .month(activeMonth.value.month - 1)
        .startOf('month');
    const monthEnd = monthStart.endOf('month');
    const minDate = dayjs().startOf('day');

    const daysInMonth = monthEnd.date();
    let allDaysDisabled = true;

    for (let day = 1; day <= daysInMonth; day++) {
        const currentDay = monthStart.date(day);
        const isBeforeMinDate = currentDay.isBefore(minDate, 'day');

        if (isBeforeMinDate) {
            continue;
        }

        const isInDisabledDates = disabledDates.value.some((disabledDate) => {
            return dayjs(disabledDate).isSame(currentDay, 'day');
        });

        if (!isInDisabledDates) {
            allDaysDisabled = false;
            break;
        }
    }

    return allDaysDisabled;
});

watch(activeMonth, (newActiveMonth, oldActiveMonth) => {
    const groupId = currentGroup.value?.group_id;
    const orgUnitId = currentOrgUnit.value?.id;

    if (onCorporate() || !props.campaignType || !campaignTypeRestricted.value) {
        return;
    }

    if (
        newActiveMonth &&
        (!oldActiveMonth ||
            newActiveMonth.month !== oldActiveMonth.month ||
            newActiveMonth.year !== oldActiveMonth.year)
    ) {
        getSendingCalendar({ groupId, orgUnitId }, props.campaignType, newActiveMonth);
    }
});

const getBoundaryHours = (allowedHours: number[]): Set<number> => {
    if (!allowedHours || allowedHours.length === 0) {
        return new Set();
    }

    const sorted = [...allowedHours].sort((a, b) => a - b);
    const boundaries = new Set<number>();

    for (let i = 0; i < sorted.length; i++) {
        const currentHour = sorted[i];
        const nextHour = sorted[i + 1];

        if (currentHour !== undefined && (nextHour === undefined || nextHour !== currentHour + 1)) {
            boundaries.add(currentHour);
        }
    }

    return boundaries;
};

const datePickerRules = computed(() => {
    const baseRules = {
        minutes: { interval: 5 },
    };

    if (!props.modelValue) {
        return {
            ...baseRules,
            hours: [],
        };
    }

    if (props.modelValue && rawCalendarDates.value) {
        const tz = getClientTz();
        const dateKey = dayjs(props.modelValue).tz(tz).format('YYYY-MM-DD');

        const isDateDisabled = disabledDates.value.some(
            (disabledDate) => dayjs(disabledDate).tz(tz).format('YYYY-MM-DD') === dateKey
        );

        if (isDateDisabled) {
            return {
                ...baseRules,
                hours: [],
            };
        } else {
            const allowedHours = allowedHoursByDate.value.get(dateKey);

            if (allowedHours && allowedHours.length > 0) {
                return {
                    ...baseRules,
                    hours: allowedHours,
                };
            }
        }
    }

    return baseRules;
});

const disabledMinutes = computed(() => {
    if (onCorporate() || !campaignTypeRestricted.value) {
        return [];
    }

    if (!props.modelValue) {
        return Array.from({ length: 12 }, (_, i) => i * 5);
    }

    const tz = getClientTz();
    const dateKey = dayjs(props.modelValue).tz(tz).format('YYYY-MM-DD');
    const allowedHours = allowedHoursByDate.value.get(dateKey);

    if (!allowedHours || allowedHours.length === 0) {
        return [];
    }

    const selectedHour = dayjs(props.modelValue).tz(tz).hour();
    const boundaryHours = getBoundaryHours(allowedHours);

    if (boundaryHours.has(selectedHour)) {
        return [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
    }

    return [];
});

const minuteAttributes = computed((): VxDateTimeMinuteAttributeConfig[] => {
    if (!props.modelValue || !rawCalendarDates.value) {
        return [];
    }

    const tz = getClientTz();
    const dateKey = dayjs(props.modelValue).tz(tz).format('YYYY-MM-DD');
    const allowedHours = allowedHoursByDate.value.get(dateKey);

    if (!allowedHours || allowedHours.length === 0) {
        return [];
    }

    const selectedHour = dayjs(props.modelValue).tz(tz).hour();
    const boundaryHours = getBoundaryHours(allowedHours);

    if (boundaryHours.has(selectedHour)) {
        return [
            {
                minutes: [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55],
                popover: {
                    placement: 'top',
                    hover: false,
                    arrow: true,
                },
            },
        ];
    }

    return [];
});

const hourAttributes = computed((): VxDateTimeHourAttributeConfig[] => {
    if (!props.modelValue || !rawCalendarDates.value) {
        return [];
    }

    const tz = getClientTz();
    const dateKey = dayjs(props.modelValue).tz(tz).format('YYYY-MM-DD');
    const blockedHours = blockedHoursByDate.value.get(dateKey);
    const isDateDisabled = disabledDates.value.some(
        (disabledDate) => dayjs(disabledDate).tz(tz).format('YYYY-MM-DD') === dateKey
    );

    if (isDateDisabled || !blockedHours || blockedHours.size === 0) {
        return [];
    }

    const attributes: VxDateTimeHourAttributeConfig[] = [];
    for (const hour of blockedHours) {
        attributes.push({
            hours: hour,
            popover: {
                placement: 'top',
                hover: false,
                arrow: true,
            },
        });
    }

    return attributes;
});

const getReasonMessage = (reason: string, dateKey: string): string => {
    if (reason === 'reserved_date') {
        const customReason = reservedDateReasons.value.get(dateKey);
        if (customReason) {
            return customReason;
        }
    }

    return REASON_MESSAGES[reason] || reason;
};

const getDayReasons = (day: { id?: string; isDisabled?: boolean }) => {
    if (!reasonsByDate.value || !day?.id) {
        return null;
    }
    const dateKey = dayjs(day.id).format('YYYY-MM-DD');
    const reasons = reasonsByDate.value.get(dateKey);
    return reasons ? Array.from(reasons) : null;
};

const adjustToEarliestAvailableTime = (selectedDate: Date) => {
    const dateKey = dayjs(selectedDate).tz(getClientTz()).format('YYYY-MM-DD');
    const allowedHours = allowedHoursByDate.value.get(dateKey);
    const selectedHour = dayjs(selectedDate).tz(getClientTz()).hour();

    if (allowedHours && allowedHours.length > 0 && !allowedHours.includes(selectedHour)) {
        const earliestHour = Math.min(...allowedHours);
        return dayjs(selectedDate).tz(getClientTz()).hour(earliestHour).minute(0).second(0).toDate();
    }

    return selectedDate;
};

const adjustMinutesForHour = (selectedDate: Date) => {
    const tz = getClientTz();
    const dateKey = dayjs(selectedDate).tz(tz).format('YYYY-MM-DD');
    const allowedHours = allowedHoursByDate.value.get(dateKey);

    if (!allowedHours || allowedHours.length === 0) {
        return selectedDate;
    }

    const selectedHour = dayjs(selectedDate).tz(tz).hour();
    const selectedMinute = dayjs(selectedDate).tz(tz).minute();
    const boundaryHours = getBoundaryHours(allowedHours);

    if (boundaryHours.has(selectedHour) && selectedMinute !== 0) {
        return dayjs(selectedDate).tz(tz).minute(0).second(0).toDate();
    }

    return selectedDate;
};

const onDateChange = (newDate?: Date) => {
    if (!newDate) {
        emit('update:modelValue', newDate);
        return;
    }

    if (!onCorporate() && campaignTypeRestricted.value) {
        const adjusted = adjustMinutesForHour(adjustToEarliestAvailableTime(newDate));

        if (adjusted.getTime() !== newDate.getTime()) {
            nextTick(() => emit('update:modelValue', adjusted));
            return;
        }
    }

    emit('update:modelValue', newDate);
};

defineExpose({
    datePicker,
});
</script>
